import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const ROLES = [
  { key: "sales", label: "Sales staff", hint: "Sales & sales orders, inventory and ledger. No purchase rates, no hisab." },
  { key: "stock", label: "Stock staff", hint: "Purchases, purchase orders, stock adjustments and Item Master." },
  { key: "viewer", label: "Viewer", hint: "Read only, can export. Cannot make any entry." },
  { key: "rates", label: "Rate only", hint: "Opens the Rate Finder alone — item name, company, quality and sale price. Nothing else." },
];

export default function Users() {
  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "sales" });
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setRows(data)).catch(() => {});

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Staff login created");
      setForm({ email: "", password: "", name: "", role: "sales" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (row, role) => {
    try {
      await api.put(`/users/${row.id}`, { role });
      load();
      toast.success("Access updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetPassword = async (row) => {
    const password = window.prompt(`New password for ${row.email} (min 6 characters)`);
    if (!password) return;
    try {
      await api.put(`/users/${row.id}`, { password });
      toast.success("Password changed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${deleteRow.id}`);
      setDeleteRow(null);
      load();
      toast.success("Staff login removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1100px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Admin</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Staff Logins</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-500">
            Staff work on your stock with their own email and password. Only you can manage logins, masters and hisab.
          </p>
        </div>

        <form onSubmit={add} className="mt-6 grid grid-cols-1 gap-4 border border-zinc-200 bg-white p-5 sm:grid-cols-2">
          <div>
            <L>Email</L>
            <Input data-testid="staff-email-input" type="email" required className="mt-2 rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="staff@example.com" />
          </div>
          <div>
            <L>Name</L>
            <Input data-testid="staff-name-input" className="mt-2 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Staff name" />
          </div>
          <div>
            <L>Password</L>
            <Input data-testid="staff-password-input" type="text" required minLength={6} className="mt-2 rounded-sm" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 characters" />
          </div>
          <div>
            <L>Access</L>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`staff-role-${r.key}`}
                  onClick={() => setForm({ ...form, role: r.key })}
                  className={`border p-2 text-left text-xs transition-colors duration-200 ${
                    form.role === r.key ? "border-zinc-950 bg-[var(--mod-primary)] text-white" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <span className="font-bold uppercase tracking-tight">{r.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-zinc-500">{ROLES.find((r) => r.key === form.role)?.hint}</p>
          </div>
          <div className="sm:col-span-2">
            <Button data-testid="staff-add-button" type="submit" disabled={saving} className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90">
              <Plus size={15} className="mr-2" /> {saving ? "Creating…" : "Create login"}
            </Button>
          </div>
        </form>

        <div className="mt-8">
          <h2 className="font-head text-lg font-bold uppercase tracking-tight">Staff ({rows.length})</h2>
          {rows.length === 0 ? (
            <p className="mt-3 text-sm text-zinc-500">No staff logins yet.</p>
          ) : (
            <div className="mt-3 divide-y divide-zinc-200 border border-zinc-200 bg-white">
              {rows.map((row) => (
                <div key={row.id} data-testid={`staff-row-${row.email}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="text-sm font-semibold">{row.name || row.email}</p>
                    <p className="text-xs text-zinc-500">{row.email}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {ROLES.map((r) => (
                      <button
                        key={r.key}
                        data-testid={`staff-set-${r.key}-${row.email}`}
                        onClick={() => changeRole(row, r.key)}
                        className={`border px-2.5 py-1 text-[11px] font-bold uppercase tracking-tight transition-colors duration-200 ${
                          row.role === r.key ? "border-zinc-950 bg-[var(--mod-primary)] text-white" : "border-zinc-200 hover:border-zinc-400"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                    <Button data-testid={`staff-password-${row.email}`} size="sm" variant="outline" className="rounded-sm" onClick={() => resetPassword(row)}>
                      <KeyRound size={13} className="mr-1.5" /> Password
                    </Button>
                    <Button data-testid={`staff-delete-${row.email}`} size="sm" variant="outline" className="rounded-sm text-red-600 hover:bg-red-50" onClick={() => setDeleteRow(row)}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this login?</AlertDialogTitle>
            <AlertDialogDescription>{deleteRow?.email} will not be able to sign in any more.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="staff-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
